import type { ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { periodLabel, type PaycheckPeriod } from '@alohamula/domain';
import { Button } from './ui';
import {
  BrandMark,
  ChatIcon,
  EnvelopeIcon,
  HomeIcon,
  MapIcon,
  RulesIcon,
  SettingsIcon,
  SortIcon,
} from './icons';
import type { RealtimeStatus } from '../realtime/useRealtime';
import './AppShell.css';

interface Destination {
  to: string;
  label: string;
  Icon: typeof HomeIcon;
}

/*
 * One label per destination, no short variants. The transactions tab used to say
 * "Ledger" while the sidebar said "Transactions" and the page title said
 * "Ledger" again — three names for one object, which is exactly the thing
 * Jakob's Law says costs you.
 *
 * Settings is deliberately absent from the phone tab bar: it is not a daily
 * destination. Chat and Map live in the sidebar on desktop — a sixth phone tab
 * would crowd the glass bar. Phones capture place in the iOS app; the web map
 * is for desktop/tablet review.
 */
const DESTINATIONS: Destination[] = [
  { to: '/', label: 'Overview', Icon: HomeIcon },
  { to: '/sort', label: 'Sort', Icon: SortIcon },
  { to: '/plan', label: 'Envelopes', Icon: EnvelopeIcon },
  { to: '/transactions', label: 'Transactions', Icon: RulesIcon },
];

const CHAT: Destination = { to: '/chat', label: 'Chat', Icon: ChatIcon };
const MAP: Destination = { to: '/map', label: 'Map', Icon: MapIcon };
const SETTINGS: Destination = { to: '/settings', label: 'Settings', Icon: SettingsIcon };
const SIDEBAR_DESTINATIONS: Destination[] = [...DESTINATIONS, CHAT, MAP, SETTINGS];

interface AppShellProps {
  /** Shown in the sidebar so "3 to sort" is visible from anywhere. */
  toSortCount: number;
  realtimeStatus: RealtimeStatus;
  email: string;
  onSignOut: () => void;
}

export function AppShell({ toSortCount, realtimeStatus, email, onSignOut }: AppShellProps) {
  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <aside className="shell__sidebar">
        <NavLink to="/" className="shell__brand">
          <BrandMark width={22} height={22} style={{ color: 'var(--color-action)' }} />
          AlohaMula
        </NavLink>

        {/* A sidebar has room, so Settings is simply the last row here. */}
        <nav className="shell__nav" aria-label="Sections">
          {SIDEBAR_DESTINATIONS.map(({ to, label, Icon }) => (
            <NavLink key={to} to={to} end={to === '/'} className="navlink">
              <Icon className="navlink__icon" />
              {label}
              {to === '/sort' && toSortCount > 0 && (
                <span className="navlink__count">{toSortCount}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="spacer" />

        <div className="stack" style={{ gap: 'var(--space-xs)', paddingInline: 'var(--space-xs)' }}>
          <SyncStatus status={realtimeStatus} />
          <span className="section__meta" style={{ overflowWrap: 'anywhere' }}>
            {email}
          </span>
          <Button emphasis="ghost" size="sm" onClick={onSignOut}>
            Sign out
          </Button>
        </div>
      </aside>

      <main className="shell__main" id="main">
        <div className="shell__content">
          <div className="shell__mobile-header">
            <div className="row" style={{ gap: 'var(--space-xs)' }}>
              <BrandMark width={20} height={20} style={{ color: 'var(--color-action)' }} />
              <span style={{ font: 'var(--text-title-2)' }}>AlohaMula</span>
            </div>
            <div className="row" style={{ gap: 'var(--space-sm)' }}>
              <SyncStatus status={realtimeStatus} />
              {/* Where Settings goes once it is off the tab bar. */}
              <NavLink to="/settings" className="icon-btn" aria-label="Settings">
                <SettingsIcon width={20} height={20} />
              </NavLink>
            </div>
          </div>
          <Outlet />
        </div>
      </main>

      <nav className="shell__tabbar" aria-label="Sections">
        {DESTINATIONS.map(({ to, label, Icon }) => (
          <NavLink key={to} to={to} end={to === '/'} className="tab">
            <span style={{ position: 'relative' }}>
              <Icon className="tab__icon" />
              {to === '/sort' && toSortCount > 0 && (
                <span className="tab__count">{toSortCount}</span>
              )}
            </span>
            {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/**
 * Sync state is never silent, and never color-only — the dot is always paired
 * with a word, because "is this up to date?" is the question this app must never
 * leave ambiguous.
 */
function SyncStatus({ status }: { status: RealtimeStatus }) {
  const copy: Record<RealtimeStatus, { text: string; modifier: string }> = {
    live: { text: 'Live', modifier: 'live' },
    connecting: { text: 'Connecting', modifier: 'connecting' },
    offline: { text: 'Not live', modifier: 'offline' },
    disabled: { text: 'Manual refresh', modifier: 'offline' },
    error: { text: 'Sync failed', modifier: 'error' },
  };
  const { text, modifier } = copy[status];

  return (
    <span className="sync">
      <span className={`sync__dot sync__dot--${modifier}`} aria-hidden="true" />
      {text}
    </span>
  );
}

export function PageHeader({
  title,
  subtitle,
  period,
  action,
}: {
  title: string;
  subtitle?: string;
  /** When given, the subtitle names the paycheck period being shown. */
  period?: PaycheckPeriod;
  action?: ReactNode;
}) {
  const caption = subtitle ?? (period ? `Paycheck ${periodLabel(period)}` : undefined);
  return (
    <header className="shell__topbar">
      <div>
        <h1 className="shell__heading">{title}</h1>
        {caption && <p className="shell__subheading">{caption}</p>}
      </div>
      <div className="spacer" />
      {action}
    </header>
  );
}
